import { useEffect, useRef, useState } from 'react'
import {
  APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT,
  listAppSuitePublishedComponentStyles,
  listAppSuitePublishedDeviceTypeModals,
  listAppSuitePublishedTemplates,
} from '../services/appsuiteApi'
import './AppSuitePublishedAssetsPage.css'

type PublishedMode = 'templates' | 'device-types' | 'components'

type AssetItem = {
  id: string
  sourceAssetId?: string
  name: string
  version?: string
  visibility?: string
  publishedAt?: string
  marketplacePreviewToken?: string
  description?: string
  snapshot?: Record<string, unknown>
}

function cleanText(input: unknown): string {
  return typeof input === 'string' ? input.trim() : ''
}

function asRecord(input: unknown): Record<string, unknown> {
  return input && typeof input === 'object' ? (input as Record<string, unknown>) : {}
}

function toMarketplaceItem(row: Record<string, unknown>): AssetItem | null {
  const id = cleanText(row.id) || cleanText(row.assetId)
  if (!id) return null
  const sourceAssetId =
    cleanText(row.assetId) ||
    cleanText(row.templateId) ||
    cleanText(row.sourceTemplateId) ||
    id
  const name =
    cleanText(row.name) ||
    cleanText(row.assetName) ||
    cleanText(row.styleName) ||
    cleanText(asRecord(asRecord(row.snapshot).modalOverride).styleName) ||
    sourceAssetId
  return {
    id,
    sourceAssetId,
    name,
    version: cleanText(row.version),
    visibility: cleanText(row.visibility) || 'public',
    publishedAt: cleanText(row.publishedAt) || cleanText(row.deployedAt),
    marketplacePreviewToken: cleanText(row.marketplacePreviewToken) || undefined,
    description: cleanText(row.description) || undefined,
    snapshot: row.snapshot && typeof row.snapshot === 'object'
      ? (row.snapshot as Record<string, unknown>)
      : undefined,
  }
}

// ── Template / Modal Card (read-only preview) ────────────────────────────────
const IFRAME_W = 402
const IFRAME_H = 874
const CARD_PAD = 20

function PreviewCard({
  item,
  previewPath,
  onPreview,
}: {
  item: AssetItem
  previewPath: string
  onPreview: (item: AssetItem) => void
}) {
  const previewRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)

  useEffect(() => {
    const el = previewRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      if (w > 0) setScale((w - CARD_PAD * 2) / IFRAME_W)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const previewHeight = scale > 0 ? Math.round(IFRAME_H * scale) + CARD_PAD * 2 : 0

  const phoneFrameStyle = scale > 0 ? {
    width: Math.round(IFRAME_W * scale),
    height: Math.round(IFRAME_H * scale),
    borderRadius: Math.round(50 * scale),
  } : undefined

  return (
    <div className="appsuite-tmpl-card">
      <div className="appsuite-tmpl-card__preview" ref={previewRef} style={{ height: previewHeight }}>
        {scale > 0 && (
          <div className="appsuite-tmpl-card__phone-frame" style={phoneFrameStyle}>
            <div className="appsuite-tmpl-card__iframe-wrap">
              <iframe
                title={item.name}
                src={previewPath}
                loading="lazy"
                tabIndex={-1}
                style={{ transform: `scale(${scale})`, width: IFRAME_W, height: IFRAME_H }}
              />
            </div>
          </div>
        )}
        <div className="appsuite-tmpl-card__overlay">
          <button
            type="button"
            className="appsuite-tmpl-card__preview-btn"
            onClick={() => onPreview(item)}
          >
            <span className="material-symbols-rounded">open_in_new</span>
            Full Preview
          </button>
        </div>
      </div>

      <div className="appsuite-tmpl-card__body">
        <div className="appsuite-tmpl-card__meta">
          <span className="appsuite-tmpl-card__name">{item.name}</span>
          {item.version ? <span className="appsuite-tmpl-card__version">v{item.version}</span> : null}
        </div>
        {item.description ? (
          <p className="appsuite-tmpl-card__desc">{item.description}</p>
        ) : null}
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AppSuitePublishedAssetsPage({
  mode,
}: {
  mode: PublishedMode
}) {
  const [items, setItems] = useState<AssetItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const title = mode === 'templates'
    ? 'Templates Marketplace'
    : mode === 'device-types'
      ? 'Device Type Marketplace'
      : 'Component Style Marketplace'

  const desc = mode === 'templates'
    ? 'Browse available app templates.'
    : mode === 'device-types'
      ? 'Browse available device modal templates.'
      : 'Browse available component styles.'

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const loader =
        mode === 'templates'
          ? listAppSuitePublishedTemplates
          : mode === 'device-types'
            ? listAppSuitePublishedDeviceTypeModals
            : listAppSuitePublishedComponentStyles
      const rows = await loader()
      const mapped = rows
        .map((row) => toMarketplaceItem(asRecord(row)))
        .filter((row): row is AssetItem => Boolean(row))
      setItems(mapped)
    } catch (e) {
      setItems([])
      setError(e instanceof Error ? e.message : '載入資產失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // Re-fetch when the active AppSuite client changes (layout sync or user-initiated switch).
  // Uses a ref so the handler always calls the latest refresh() closure without needing it
  // in the dependency array.
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(() => {
    const handler = () => void refreshRef.current()
    window.addEventListener(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, handler)
    return () => window.removeEventListener(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, handler)
  }, [])

  const handlePreview = (item: AssetItem) => {
    const assetId = item.sourceAssetId || item.id
    const path = mode === 'device-types'
      ? `/appsuite/modal-preview/${assetId}`
      : `/appsuite/tmpl-preview/${assetId}`
    window.open(path, '_blank', 'noopener,noreferrer')
  }

  const showCardGrid = mode === 'templates' || mode === 'device-types'

  return (
    <div className="appsuite-published-page appfactory-page-content">
      <div className="appsuite-published-header">
        <h2>{title}</h2>
        <p>{desc}</p>
      </div>

      {loading ? <div className="appsuite-published-loading">Loading assets...</div> : null}
      {error ? <div className="appsuite-published-error">{error}</div> : null}

      {!loading && !error && items.length === 0 ? (
        <div className="appsuite-published-empty">
          目前沒有可用的 published 資產（或權限尚未開通）。
        </div>
      ) : null}

      {showCardGrid && !loading && !error && items.length > 0 ? (
        <div className="appsuite-tmpl-grid">
          {items.map((item) => {
            const assetId = item.sourceAssetId || item.id
            const previewPath = mode === 'device-types'
              ? `/appsuite/modal-preview/${assetId}`
              : `/appsuite/tmpl-preview/${assetId}`
            return (
              <PreviewCard
                key={item.id}
                item={item}
                previewPath={previewPath}
                onPreview={handlePreview}
              />
            )
          })}
        </div>
      ) : null}

      {!showCardGrid && !loading && !error && items.length > 0 ? (
        <div className="appsuite-published-table-wrap">
          <table className="appsuite-published-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Asset ID</th>
                <th>Version</th>
                <th>Visibility</th>
                <th>Published At</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.sourceAssetId || item.id}</td>
                  <td>{item.version || '-'}</td>
                  <td>{item.visibility || '-'}</td>
                  <td>{item.publishedAt || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
